import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    IconButton,
    InputAdornment,
    Stack,
    Alert,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    Select,
    MenuItem,
    Switch,
} from '@mui/material';
import {
    Clear as ClearIcon,
    CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import PageHeader from './PageHeader';
import { getStoredToken, getCompanyPickerConfig } from '../utils/api';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { useAuth } from '../contexts/AuthContext';
import { baseURL as API_BASE_URL } from '../config/api';
import { resolveCompanyLogoSrc } from '../utils/webviewAudio';

// Default values
const DEFAULT_WELCOME_MESSAGE = 'Welcome to Kivo.ai';
const DEFAULT_LOGO = '/assets/icons/kivo-fav-icon.png';

const Settings = () => {
    const { confirm } = useConfirmDialog();
    const { permissions } = useAuth();
    const [welcomeMessage, setWelcomeMessage] = useState(DEFAULT_WELCOME_MESSAGE);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(DEFAULT_LOGO);
    const [isDragging, setIsDragging] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [companyOptions, setCompanyOptions] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [selectedCompanyName, setSelectedCompanyName] = useState('');
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [settingActive, setSettingActive] = useState(false);
    const [enableVoicePlayback, setEnableVoicePlayback] = useState(true);
    
    // Function to call set-active API when company is selected
    const setActiveCompany = async (companyId) => {
        if (!companyId) return false;
        
        setSettingActive(true);
        try {
            const token = getStoredToken();
            const headers = {
                'Content-Type': 'application/json',
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_BASE_URL}/api/v1/settings/company/set-active?company_id=${encodeURIComponent(companyId)}`, {
                method: 'POST',
                headers,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Failed to set active company');
            }

            const data = await response.json();
            console.log('[Settings] Company set as active:', data);
            return true;
        } catch (err) {
            console.error('Error setting active company:', err);
            setError(`Failed to set company as active: ${err.message}`);
            return false;
        } finally {
            setSettingActive(false);
        }
    };
    
    // Load companies from Kivo config for dropdown and fetch active company
    useEffect(() => {
        const loadCompanies = async () => {
            setOptionsLoading(true);
            try {
                // First, fetch the currently active company from the database
                const token = getStoredToken();
                const headers = {};
                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }
                
                let activeCompanyId = null;
                try {
                    const activeResponse = await fetch(`${API_BASE_URL}/api/v1/settings/company`, { headers });
                    if (activeResponse.ok) {
                        const activeData = await activeResponse.json();
                        activeCompanyId = activeData.company_id;
                        console.log('[Settings] Active company from DB:', activeCompanyId);
                    }
                } catch (err) {
                    console.warn('Could not fetch active company:', err);
                }
                
                // Then load company options (kivo_config or companies table)
                const res = await getCompanyPickerConfig(permissions);
                const configs = res?.configs || (Array.isArray(res) ? res : []);
                if (configs.length) {
                    const unique = [];
                    const seen = new Set();
                    configs.forEach((cfg) => {
                        const cid = (cfg.company_id || '').toString();
                        const name = cfg.company_name || cfg.company || cfg.companyId || cid || 'Unknown Company';
                        const key = cid || name.toLowerCase();
                        if (key && !seen.has(key)) {
                            seen.add(key);
                            unique.push({
                                company_id: cid,
                                company_name: name,
                                config_id: cfg.config_id,
                            });
                        }
                    });
                    setCompanyOptions(unique);
                    
                    // Set initial selection: use active company from DB, otherwise first option
                    if (!selectedCompanyId && unique.length) {
                        // Check if active company from DB is in our options
                        const activeMatch = activeCompanyId 
                            ? unique.find(c => c.company_id === activeCompanyId) 
                            : null;
                        
                        if (activeMatch) {
                            setSelectedCompanyId(activeMatch.company_id);
                            setSelectedCompanyName(activeMatch.company_name || '');
                        } else {
                            // Fall back to first option
                            setSelectedCompanyId(unique[0].company_id || '');
                            setSelectedCompanyName(unique[0].company_name || '');
                        }
                    }
                }
            } catch (err) {
                console.error('Failed to load Kivo company configs', err);
            } finally {
                setOptionsLoading(false);
            }
        };
        loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [permissions]);

    // Derive selected company name
    const selectedCompany = useMemo(() => {
        if (!selectedCompanyId) return null;
        return companyOptions.find((c) => c.company_id === selectedCompanyId) || null;
    }, [companyOptions, selectedCompanyId]);

    useEffect(() => {
        if (selectedCompany?.company_name) {
            setSelectedCompanyName(selectedCompany.company_name);
        }
    }, [selectedCompany]);

    // Fetch banner settings for the selected company only (avoid duplicate GET /settings/company
    // without company_id — the mount effect above already loads active company + Kivo config).
    useEffect(() => {
        if (!selectedCompanyId) {
            setLoading(false);
            return;
        }

        const fetchSettings = async () => {
            setLoading(true);
            setError(null);

            try {
                const token = getStoredToken();
                const headers = {};
                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }

                const query = `?company_id=${encodeURIComponent(selectedCompanyId)}`;
                const response = await fetch(`${API_BASE_URL}/api/v1/settings/company${query}`, {
                    headers,
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch company settings');
                }

                const data = await response.json();

                // Set welcome message from description, or use default
                const description = data.description?.trim();
                setWelcomeMessage(description || DEFAULT_WELCOME_MESSAGE);

                // Set logo preview if logo exists, or use default
                setLogoPreview(resolveCompanyLogoSrc(data.logo, API_BASE_URL, selectedCompanyId, DEFAULT_LOGO));

                // Set voice playback toggle (default true if not set)
                setEnableVoicePlayback(data.enable_voice_playback !== false);
            } catch (err) {
                console.error('Error fetching settings:', err);
                setError('Failed to load settings. Using defaults.');
                setWelcomeMessage(DEFAULT_WELCOME_MESSAGE);
                setLogoPreview(DEFAULT_LOGO);
            } finally {
                setLoading(false);
            }
        };

        fetchSettings();
    }, [selectedCompanyId]);

    const pageLoading = optionsLoading || (!!selectedCompanyId && loading);

    const handleClearMessage = () => {
        setWelcomeMessage('');
    };

    const handleFileChange = (file) => {
        if (file && file.type.startsWith('image/')) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        handleFileChange(file);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleBrowseClick = () => {
        document.getElementById('logo-file-input').click();
    };

    const handleFileInputChange = (e) => {
        const file = e.target.files[0];
        handleFileChange(file);
    };

    const handleSaveSettings = async () => {
        const approved = await confirm({
            title: 'Confirm Save Settings',
            message: 'Are you sure you want to save banner settings?',
            confirmLabel: 'Confirm',
            danger: false,
        });
        if (!approved) return;

        setSaving(true);
        setError(null);
        setSuccess(null);

        try {
            const formData = new FormData();

            // Add description if changed
            if (welcomeMessage) {
                formData.append('description', welcomeMessage);
            }

            // Include company selection (id & name) so backend can upsert
            if (selectedCompanyId) {
                formData.append('company_id', selectedCompanyId);
            }
            if (selectedCompanyName) {
                formData.append('company_name', selectedCompanyName);
            }

            // Add logo file if selected
            if (logoFile) {
                formData.append('logo', logoFile);
            }

            // Include voice playback toggle
            formData.append('enable_voice_playback', enableVoicePlayback ? 'true' : 'false');

            // Get authentication token
            const token = getStoredToken();
            const headers = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_BASE_URL}/api/v1/settings/company`, {
                method: 'PUT',
                headers: headers,
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to save settings');
            }

            const data = await response.json();
            setSuccess('Settings saved successfully!');

            // Update logo preview if new logo was saved, otherwise keep current
            if (data.company && data.company.logo) {
                setLogoPreview(resolveCompanyLogoSrc(
                    data.company.logo,
                    API_BASE_URL,
                    data.company.company_id || selectedCompanyId,
                    DEFAULT_LOGO,
                ));
            } else if (data.company && !data.company.logo) {
                // If logo was removed, use default
                setLogoPreview(DEFAULT_LOGO);
            }

            // Update welcome message if it was saved
            if (data.company) {
                const description = data.company.description?.trim();
                setWelcomeMessage(description || DEFAULT_WELCOME_MESSAGE);
                if (data.company.company_id) {
                    setSelectedCompanyId(data.company.company_id);
                }
                if (data.company.company_name) {
                    setSelectedCompanyName(data.company.company_name);
                }
                if (data.company.enable_voice_playback !== undefined) {
                    setEnableVoicePlayback(data.company.enable_voice_playback !== false);
                }
            } else {
                setWelcomeMessage(DEFAULT_WELCOME_MESSAGE);
            }

            // Clear the file input after successful save
            setLogoFile(null);

        } catch (err) {
            console.error('Error saving settings:', err);
            setError(err.message || 'Failed to save settings. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (pageLoading) {
        return (
            <Box sx={{ flexGrow: 1, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1, p: 0 }}>
            <Container maxWidth="md" sx={{ mt: 0 }}>
                <PageHeader
                    title="Banner Setting"
                    subtitle="Configure the welcome banner message, logo, and related options for the selected company."
                />
          
                <Stack spacing={4}>
                    {/* Success/Error Messages */}
                    {success && (
                        <Alert severity="success" onClose={() => setSuccess(null)}>
                            {success}
                        </Alert>
                    )}
                    {error && (
                        <Alert severity="error" onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {/* Company Selection */}
                    <Paper
                        elevation={3}
                        sx={{
                            p: 4,
                            borderRadius: 3,
                            backgroundColor: '#ffffff',
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                mb: 3,
                                fontWeight: 600,
                                color: '#40518A',
                            }}
                        >
                            Select Company (from Kivo Config)
                        </Typography>
                        <FormControl fullWidth>
                            <InputLabel id="company-select-label">Company</InputLabel>
                            <Select
                                labelId="company-select-label"
                                value={selectedCompanyId}
                                label="Company"
                                onChange={async (e) => {
                                    const value = e.target.value;
                                    setSelectedCompanyId(value);
                                    const match = companyOptions.find((c) => c.company_id === value);
                                    setSelectedCompanyName(match?.company_name || '');
                                    
                                    // Call API to set this company as active in the database
                                    const success = await setActiveCompany(value);
                                    if (success) {
                                        setSuccess(`Company "${match?.company_name || value}" is now the active company`);
                                    }
                                }}
                                disabled={optionsLoading || companyOptions.length === 0 || settingActive}
                            >
                                {companyOptions.map((option) => (
                                    <MenuItem key={option.company_id || option.config_id} value={option.company_id}>
                                        {option.company_name || option.company_id || 'Unnamed Company'}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {optionsLoading && (
                            <Typography variant="body2" sx={{ mt: 1, color: '#64748B' }}>
                                Loading companies...
                            </Typography>
                        )}
                        {!optionsLoading && companyOptions.length === 0 && (
                            <Typography variant="body2" sx={{ mt: 1, color: '#d32f2f' }}>
                                No Kivo companies found. Connect Kivo first.
                            </Typography>
                        )}
                    </Paper>

                    {/* Welcome Message Section */}
                    <Paper
                        elevation={3}
                        sx={{
                            p: 4,
                            borderRadius: 3,
                            backgroundColor: '#ffffff',
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                mb: 3,
                                fontWeight: 600,
                                color: '#40518A',
                            }}
                        >
                            Welcome Message
                        </Typography>

                        <TextField
                            fullWidth
                            value={welcomeMessage}
                            onChange={(e) => setWelcomeMessage(e.target.value)}
                            placeholder="Enter welcome message"
                            variant="outlined"
                            multiline
                            rows={3}
                            InputProps={{
                                endAdornment: welcomeMessage && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={handleClearMessage}
                                            edge="end"
                                            sx={{
                                                color: '#40518A',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(64, 81, 138, 0.1)',
                                                },
                                            }}
                                        >
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    '& fieldset': {
                                        borderColor: '#e0e0e0',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#40518A',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#40518A',
                                    },
                                },
                            }}
                        />
                    </Paper>

                    {/* Logo Upload Section */}
                    <Paper
                        elevation={3}
                        sx={{
                            p: 4,
                            borderRadius: 3,
                            backgroundColor: '#ffffff',
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                mb: 3,
                                fontWeight: 600,
                                color: '#40518A',
                            }}
                        >
                            Upload Logo
                        </Typography>

                        {/* Dropbox Area */}
                        <Box
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            sx={{
                                border: '2px dashed',
                                borderColor: isDragging ? '#40518A' : '#2563EB',
                                borderRadius: 3,
                                p: 6,
                                textAlign: 'center',
                                backgroundColor: isDragging ? 'rgba(64, 81, 138, 0.05)' : '#F8FAFC',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    borderColor: '#40518A',
                                    backgroundColor: 'rgba(64, 81, 138, 0.05)',
                                },
                            }}
                        >
                            {/* Upload Icon */}
                            <CloudUploadIcon
                                sx={{
                                    fontSize: 64,
                                    color: '#2563EB',
                                    mb: 2,
                                }}
                            />

                            {/* Drop Text */}
                            <Typography
                                variant="h6"
                                sx={{
                                    color: '#1E293B',
                                    fontWeight: 600,
                                    mb: 1,
                                }}
                            >
                                Drop your logo here
                            </Typography>

                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#64748B',
                                    mb: 3,
                                }}
                            >
                                or click to browse
                            </Typography>

                            {/* Browse Files Button */}
                            <Button
                                variant="contained"
                                onClick={handleBrowseClick}
                                sx={{
                                    backgroundColor: '#2563EB',
                                    color: '#ffffff',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 2,
                                    '&:hover': {
                                        backgroundColor: '#1D4ED8',
                                    },
                                }}
                            >
                                Browse Files
                            </Button>

                            {/* Hidden File Input */}
                            <input
                                id="logo-file-input"
                                type="file"
                                accept="image/*"
                                onChange={handleFileInputChange}
                                style={{ display: 'none' }}
                            />
                        </Box>

                        {/* Supported Formats */}
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#64748B',
                                mt: 2,
                            }}
                        >
                            Supported formats: PNG, JPG, JPEG, SVG (Max 5MB)
                        </Typography>

                        {/* Logo Preview - Always show current logo */}
                        <Box
                            sx={{
                                mt: 3,
                                p: 3,
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                backgroundColor: '#f9f9f9',
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    mb: 2,
                                    fontWeight: 600,
                                    color: '#40518A',
                                }}
                            >
                                Current Logo:
                            </Typography>
                            <Box
                                component="img"
                                src={logoPreview}
                                alt="Logo Preview"
                                sx={{
                                    maxWidth: '100%',
                                    maxHeight: 200,
                                    objectFit: 'contain',
                                    display: 'block',
                                    margin: '0 auto',
                                }}
                            />
                            {logoFile && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: 'block',
                                        textAlign: 'center',
                                        mt: 1,
                                        color: '#64748B',
                                    }}
                                >
                                    New file selected: {logoFile.name}
                                </Typography>
                            )}
                            {!logoFile && logoPreview === DEFAULT_LOGO && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: 'block',
                                        textAlign: 'center',
                                        mt: 1,
                                        color: '#64748B',
                                        fontStyle: 'italic',
                                    }}
                                >
                                    (Default logo - upload a new logo to replace)
                                </Typography>
                            )}
                        </Box>
                    </Paper>

                    {/* Voice Playback Toggle */}
                    <Paper
                        elevation={3}
                        sx={{
                            p: 4,
                            borderRadius: 3,
                            backgroundColor: '#ffffff',
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                mb: 1,
                                fontWeight: 600,
                                color: '#40518A',
                            }}
                        >
                            Kiosk Speaker Voice
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2, color: '#64748B' }}>
                            Controls voice playback on Attendance Machine if enabled else it will be played on the browser.
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={enableVoicePlayback}
                                    onChange={(e) => setEnableVoicePlayback(e.target.checked)}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: '#40518A',
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: '#40518A',
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography variant="body1" sx={{ fontWeight: 500, color: '#1E293B' }}>
                                    {enableVoicePlayback ? 'Enabled' : 'Disabled'}
                                </Typography>
                            }
                        />
                    </Paper>

                    {/* Save Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            onClick={handleSaveSettings}
                            disabled={saving}
                            sx={{
                                backgroundColor: '#40518A',
                                color: '#ffffff',
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 6,
                                py: 1.5,
                                borderRadius: 2,
                                '&:hover': {
                                    backgroundColor: '#2f3d6b',
                                },
                                '&:disabled': {
                                    backgroundColor: '#ccc',
                                },
                            }}
                        >
                            {saving ? <CircularProgress size={24} color="inherit" /> : 'Save Settings'}
                        </Button>
                    </Box>
                </Stack>
            </Container>
        </Box>
    );
};

export default Settings;
